import React, { useEffect, useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { clampPct } from '@restorant/core';
import { fontStyle, radius, scaleFont, useColors, useTextScale } from '../theme';
import { useT } from '../i18n';
import { TextInput } from 'react-native';
import { T } from './Text';

export interface PromptRequest {
  title: string;
  hint?: string;
  /** Pre-filled value. */
  initial?: string;
  /** `percent` clamps the result to 0-90; `text` passes it through trimmed. */
  mode?: 'percent' | 'text';
  placeholder?: string;
  submitLabel?: string;
  onSubmit: (value: string) => void;
}

/**
 * Stand-in for window.prompt(), which does not exist in React Native. Used for
 * custom discounts and for adding a table number.
 */
export function PromptModal({
  request,
  onClose,
}: {
  request: PromptRequest | null;
  onClose: () => void;
}) {
  const colors = useColors();
  const scale = useTextScale();
  const t = useT();
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(request?.initial ?? '');
  }, [request]);

  if (!request) return <Modal visible={false} transparent />;

  const percent = (request.mode ?? 'percent') === 'percent';

  const submit = () => {
    const out = percent ? String(clampPct(value)) : value.trim();
    if (!percent && !out) return onClose();
    request.onSubmit(out);
    onClose();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: colors.scrim,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        {/* Swallow the press so tapping the card does not close the modal. */}
        <Pressable
          onPress={() => {}}
          style={{
            width: '100%',
            maxWidth: 380,
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.line,
            borderRadius: radius.lg,
            padding: 20,
            gap: 14,
          }}
        >
          <T size={15.5} weight={600} serif>
            {request.title}
          </T>
          {request.hint ? (
            <T size={12} color={colors.textFaint}>
              {request.hint}
            </T>
          ) : null}

          <TextInput
            value={value}
            onChangeText={setValue}
            onSubmitEditing={submit}
            keyboardType={percent ? 'number-pad' : 'default'}
            inputMode={percent ? 'numeric' : 'text'}
            autoFocus
            placeholder={request.placeholder ?? (percent ? '0' : '')}
            placeholderTextColor={colors.textFaint}
            style={[
              {
                backgroundColor: colors.surface2,
                borderWidth: 1,
                borderColor: colors.line,
                borderRadius: radius.md,
                paddingVertical: 12,
                paddingHorizontal: 14,
                color: colors.text,
                fontSize: scaleFont(16, scale),
                outlineStyle: 'none',
              } as any,
              fontStyle(600),
            ]}
          />

          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Pressable
              onPress={onClose}
              style={{
                flex: 1,
                borderWidth: 1,
                borderColor: colors.line,
                borderRadius: radius.md,
                paddingVertical: 12,
                alignItems: 'center',
              }}
            >
              <T size={13} weight={700} color={colors.textDim}>
                {t('cancel')}
              </T>
            </Pressable>
            <Pressable
              onPress={submit}
              style={{
                flex: 1,
                backgroundColor: colors.gold,
                borderRadius: radius.md,
                paddingVertical: 12,
                alignItems: 'center',
              }}
            >
              <T size={13} weight={800} color={colors.onGold}>
                {request.submitLabel ?? t('apply')}
              </T>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
